import React, { useState, useEffect } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '32px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '24px', maxWidth: '600px',
  },
  sectionTitle: {
    fontSize: '13px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  avatar: {
    width: '72px', height: '72px', borderRadius: '50%',
    backgroundColor: '#0052CC', color: '#fff',
    fontSize: '28px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '20px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '16px',
    boxSizing: 'border-box', outline: 'none',
  },
  readOnly: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #e5e7eb', fontSize: '14px', marginBottom: '16px',
    boxSizing: 'border-box', backgroundColor: '#f9fafb', color: '#6b7280',
  },
  saveBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '10px 24px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer',
  },
  success: {
    backgroundColor: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a',
    borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px',
  },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px',
  },
  verifiedBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '11px', fontWeight: '700', color: '#16a34a',
    backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: '20px',
    marginLeft: '8px',
  },
  unverifiedBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '11px', fontWeight: '700', color: '#d97706',
    backgroundColor: '#fef3c7', padding: '2px 8px', borderRadius: '20px',
    marginLeft: '8px',
  },
};

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

export default function Profile() {
  const { currentUser, userProfile } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [organization, setOrganization] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Populate fields when userProfile loads
  useEffect(() => {
    if (userProfile) {
      setDisplayName(userProfile.displayName || '');
      setOrganization(userProfile.organization || '');
    }
  }, [userProfile]);

  async function handleSave() {
    if (!currentUser) return;
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: displayName.trim(),
        organization: organization.trim(),
        role: userProfile?.role || 'other',
        permissions: userProfile?.permissions || ['read', 'write'],
        created_at: userProfile?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, { merge: true });
      setSuccess('Profile updated successfully.');
    } catch (err) {
      console.error('Profile update error:', err);
      setError(`Failed to update profile: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  const initials = getInitials(displayName || currentUser?.email || '');

  return (
    <AppLayout>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>My Profile</div>
          <div style={styles.sub}>Manage your account information</div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>Account</div>

        <div style={styles.avatar}>{initials}</div>

        {success && <div style={styles.success}>{success}</div>}
        {error && <div style={styles.error}>{error}</div>}

        <label style={styles.label}>Display Name</label>
        <input
          style={styles.input}
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          placeholder="Your full name"
        />

        <label style={styles.label}>Organization</label>
        <input
          style={styles.input}
          value={organization}
          onChange={e => setOrganization(e.target.value)}
          placeholder="University, company, or institution"
        />

        <label style={styles.label}>
          Email
          {currentUser?.emailVerified
            ? <span style={styles.verifiedBadge}>✓ Verified</span>
            : <span style={styles.unverifiedBadge}>⚠ Unverified</span>
          }
        </label>
        <div style={styles.readOnly}>{currentUser?.email}</div>

        <label style={styles.label}>Role</label>
        <label style={styles.label}>Plan</label>
        <div style={{
          ...styles.readOnly,
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <span style={{
            fontSize: '12px', fontWeight: '700', padding: '2px 10px',
            borderRadius: '20px',
            backgroundColor: userProfile?.plan === 'enterprise' ? '#fef3c7' :
                             userProfile?.plan === 'professional' ? '#dbeafe' : '#f3f4f6',
            color: userProfile?.plan === 'enterprise' ? '#d97706' :
                   userProfile?.plan === 'professional' ? '#0052CC' : '#6b7280',
          }}>
            {(userProfile?.plan || 'standard').charAt(0).toUpperCase() + (userProfile?.plan || 'standard').slice(1)}
          </span>
          {userProfile?.plan === 'professional' && (
            <button
              style={{
                marginTop: '8px', padding: '8px 16px', borderRadius: '8px',
                backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb',
                fontSize: '13px', fontWeight: '600', color: '#374151',
                cursor: 'pointer', fontFamily: 'inherit', display: 'block',
              }}
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/api/stripe/portal`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ owner_id: currentUser.uid }),
                  });
                  const data = await res.json();
                  if (data.url) {
                    window.location.href = data.url;
                  } else {
                    alert(data.error || 'Could not open billing portal. Please try again.');
                  }
                } catch (err) {
                  alert('Something went wrong. Please try again.');
                }
              }}
            >
              💳 Manage Subscription
            </button>
          )}
        </div>

        <label style={styles.label}>Member Since</label>
        <div style={styles.readOnly}>
          {userProfile?.created_at
            ? new Date(userProfile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : '—'}
        </div>

        <button style={styles.saveBtn} onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </AppLayout>
  );
}
